import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Clock, Award, Plus, CheckCircle2, Hourglass } from 'lucide-react';
import { centerToast } from './CenterToast';
import { getMyVolunteering, getSelfLogRoles, selfLogHours } from '../lib/vms';

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const inputCls = 'w-full h-10 px-3 text-sm rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary/40';

function LogHoursForm({ roles, onDone, onCancel }) {
  const [roleId, setRoleId] = useState(roles[0]?.id || '');
  const [date, setDate] = useState(todayISO());
  const [hours, setHours] = useState('2');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setError(''); setSubmitting(true);
    const r = await selfLogHours({ roleId, sessionDate: date, hoursLogged: Number(hours), notes });
    setSubmitting(false);
    if (r.ok) { centerToast(r.message || 'Hours logged', '⏱️'); onDone(); }
    else setError(r.error);
  };

  return (
    <div className="mt-3 space-y-3 rounded-xl border border-border p-3 bg-muted/30" data-testid="log-hours-form">
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div>
        <label className="text-xs font-medium text-muted-foreground">Activity</label>
        <select className={inputCls} value={roleId} onChange={(e) => setRoleId(e.target.value)}>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-xs font-medium text-muted-foreground">Date</label>
          <input type="date" className={inputCls} value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Hours</label>
          <input type="number" step="0.5" min="0.5" max="24" className={inputCls} value={hours} onChange={(e) => setHours(e.target.value)} />
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground">Notes (optional)</label>
        <input className={inputCls} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="what you did" />
      </div>
      <div className="flex gap-2">
        <Button size="sm" className="flex-1 h-9" disabled={submitting || !roleId} onClick={submit}>
          {submitting ? 'Logging…' : 'Log hours'}
        </Button>
        <Button size="sm" variant="outline" className="h-9" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export default function MyVolunteering() {
  const [data, setData] = useState(undefined); // undefined = loading, null = unavailable
  const [roles, setRoles] = useState([]);
  const [logging, setLogging] = useState(false);

  const load = () => getMyVolunteering().then(setData).catch(() => setData(null));
  useEffect(() => { load(); getSelfLogRoles().then(setRoles); }, []);

  if (data === undefined) {
    return <div className="px-4 pt-4"><div className="h-28 rounded-xl bg-muted/50 animate-pulse" /></div>;
  }
  if (data === null) return null; // not signed in / VMS unreachable — the sign-up list below still renders

  if (!data.enrolled) {
    return (
      <div className="px-4 pt-4">
        <Card><CardContent className="p-4">
          <p className="text-sm font-semibold">Start tracking your volunteering</p>
          <p className="text-xs text-muted-foreground mt-1">Join a volunteer opportunity and your hours, phase and recognitions will show up here.</p>
        </CardContent></Card>
      </div>
    );
  }

  const canLog = roles.length > 0;
  return (
    <div className="px-4 pt-4 space-y-3" data-testid="my-volunteering">
      {/* Summary */}
      <Card style={{ backgroundColor: '#E8F5E9' }} className="border-primary/20">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Your volunteering</p>
              <p className="font-bold text-lg leading-tight truncate">{data.phaseLabel}</p>
            </div>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary bg-white/70 rounded-full px-2.5 py-1 whitespace-nowrap">
              <CheckCircle2 className="h-3.5 w-3.5" /> {data.status}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3">
            <div className="rounded-lg bg-white/70 p-2.5 text-center">
              <p className="text-2xl font-bold text-primary leading-none flex items-center justify-center gap-1"><Clock className="h-4 w-4" />{data.totalHours}</p>
              <p className="text-[11px] text-muted-foreground mt-1">Approved hours</p>
            </div>
            <div className="rounded-lg bg-white/70 p-2.5 text-center">
              <p className="text-2xl font-bold text-primary leading-none">{data.sessionsCount}</p>
              <p className="text-[11px] text-muted-foreground mt-1">Sessions</p>
            </div>
            <div className="rounded-lg bg-white/70 p-2.5 text-center">
              <p className="text-2xl font-bold text-primary leading-none flex items-center justify-center gap-1"><Award className="h-4 w-4" />{data.recognitions.length}</p>
              <p className="text-[11px] text-muted-foreground mt-1">Recognitions</p>
            </div>
          </div>
          {data.pendingCount > 0 && (
            <p className="text-[11px] text-amber-700 mt-2.5 flex items-center gap-1">
              <Hourglass className="h-3.5 w-3.5" /> {data.pendingHours}h awaiting your coordinator's approval
            </p>
          )}
        </CardContent>
      </Card>

      {/* Log hours */}
      {canLog && !logging && (
        <Button variant="outline" className="w-full h-10" data-testid="open-log-hours" onClick={() => setLogging(true)}>
          <Plus className="h-4 w-4 mr-1" /> Log volunteer hours
        </Button>
      )}
      {canLog && logging && (
        <LogHoursForm roles={roles} onCancel={() => setLogging(false)} onDone={() => { setLogging(false); load(); }} />
      )}

      {/* Recognitions detail */}
      {data.recognitions.length > 0 && (
        <div className="space-y-2">
          {data.recognitions.slice(0, 3).map((r, i) => (
            <Card key={i}><CardContent className="p-3 flex items-center gap-3">
              <Award className="h-4 w-4 text-[#D4A843] flex-shrink-0" />
              <span className="flex-1 text-sm font-medium truncate">{r.description || r.type}</span>
              <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(r.awardedAt).toLocaleDateString('en-SG', { month: 'short', year: 'numeric' })}</span>
            </CardContent></Card>
          ))}
        </div>
      )}
    </div>
  );
}
